import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  ArrowLeft, Search, Loader2, Sparkles, X, ChevronRight,
} from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';
import { BrandLogo } from '../components/BrandLogo';

const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const PRICE_BANDS = [
  { label: 'Under $10', min: 0, max: 10 },
  { label: '$10–20', min: 10, max: 20 },
  { label: '$20–40', min: 20, max: 40 },
  { label: '$40+', min: 40, max: null },
];

const PublicCatalog = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [markup, setMarkup] = useState(2.1);
  useEffect(() => { axios.get('/public/catalog-pricing').then((r) => setMarkup(r.data.garment_markup || 2.1)).catch(() => {}); }, []);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ brands: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ brand: null, category: null, priceBand: null });
  const debounceRef = useRef(null);

  const retriesRef = useRef(0);
  const MAX_LOADING_RETRIES = 20; // ~60s of cold-start grace

  const load = async (q = '', filts = filters, limit = 30) => {
    setLoading(true);
    try {
      const params = { q, limit };
      if (filts.brand) params.brand = filts.brand;
      if (filts.category) params.category = filts.category;
      if (filts.priceBand) {
        const band = PRICE_BANDS.find((b) => b.label === filts.priceBand);
        if (band) {
          params.min_price = band.min;
          if (band.max != null) params.max_price = band.max;
        }
      }
      const res = await axios.get('/public/catalog/search', { params });
      // Backend returns { loading: true } during cold-start while SanMar
      // SFTP is still populating the cache. Retry every 3s up to ~60s total.
      if (res.data.loading) {
        setResults([]);
        setTotal(0);
        setFacets({ brands: [], categories: [] });
        if (retriesRef.current < MAX_LOADING_RETRIES) {
          retriesRef.current += 1;
          setTimeout(() => load(q, filts, limit), 3000);
        } else {
          toast.error("Catalog is still warming up. Please refresh in a minute.");
        }
        return;
      }
      retriesRef.current = 0;
      setResults(res.data.results || []);
      setTotal(res.data.total || 0);
      setFacets(res.data.facets || { brands: [], categories: [] });
    } catch (e) {
      toast.error('Catalog failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load('', filters); /* initial */ }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(query.trim(), filters), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, filters]);

  const toggleFilter = (key, value) => {
    setFilters((f) => ({ ...f, [key]: f[key] === value ? null : value }));
  };
  const clearFilters = () => setFilters({ brand: null, category: null, priceBand: null });
  const hasFilters = !!(filters.brand || filters.category || filters.priceBand);

  const openProduct = (p) => setSelected(p);

  const pickColorAndDesign = (product, color) => {
    // Route to Design Now with the garment pre-selected.
    const params = new URLSearchParams({
      styleID: product.styleID,
      styleName: product.styleName || '',
      brand: product.brandName || '',
      color: color?.colorName || '',
      colorHex: color?.hex || '',
      imageUrl: product.styleImageUrl || '',
      basePrice: String(product.basePrice || 0),
    });
    navigate(`/design?${params.toString()}`);
  };

  const handleImgError = (e) => {
    e.currentTarget.style.opacity = '0.15';
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0A0A0A', color: '#fff', fontFamily: "'DM Sans', sans-serif" }} data-testid="public-catalog">
      <style>{`
        .heading-font { font-family: 'Barlow Condensed', sans-serif; letter-spacing: -0.01em; }
        .overline { font-family: 'Barlow Condensed', sans-serif; letter-spacing: 0.18em; text-transform: uppercase; }
      `}</style>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 50, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: '#fff' }} data-testid="catalog-nav-home">
            <BrandLogo height={36} /><span style={{display:'none'}} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <span className="heading-font" style={{ fontSize: 20, fontWeight: 900, textTransform: 'uppercase' }}>{brandLogo() ? '' : ''}</span>
          </Link>
          <Button onClick={() => navigate('/')} variant="ghost" style={{ color: 'rgba(255,255,255,0.7)' }} data-testid="catalog-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      {/* Hero + Search */}
      <div style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', background: 'linear-gradient(180deg, #0A0A0A, #141414)' }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '48px 24px' }}>
          <div className="overline" style={{ color: '#D90000', fontSize: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ height: 1, width: 48, background: '#D90000' }} /> Browse & Design
          </div>
          <h1 className="heading-font" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 900, textTransform: 'uppercase', lineHeight: 0.95, marginBottom: 12 }}>
            The Catalog. <span style={{ color: '#D90000' }}>Design any shirt.</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.6)', maxWidth: 640, marginBottom: 32, fontSize: 15 }}>
            Browse thousands of blank apparel styles. Click any product to design your own — instant pricing included.
          </p>
          <div style={{ position: 'relative', maxWidth: 600 }}>
            <Search size={18} style={{ position: 'absolute', left: 18, top: 15, color: 'rgba(255,255,255,0.4)' }} />
            <Input
              placeholder="Search: 'PC61', 'nike polo', 'Sport-Tek hoodie'…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="catalog-search-input"
              style={{ paddingLeft: 46, height: 48, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 4, fontSize: 15 }}
            />
            {loading && <Loader2 size={16} className="animate-spin" style={{ position: 'absolute', right: 16, top: 16, color: 'rgba(255,255,255,0.5)' }} />}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
            {total.toLocaleString()} products indexed · Live SanMar catalog
          </div>
        </div>
      </div>

      {/* Filter chips row */}
      {(facets.brands.length > 0 || facets.categories.length > 0) && (
        <div style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: '#0F0F0F' }}>
          <div style={{ maxWidth: 1400, margin: '0 auto', padding: '18px 24px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }} data-testid="catalog-filters">
            <span className="overline" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginRight: 6 }}>Brand</span>
            {facets.brands.slice(0, 6).map((b) => (
              <button
                key={b.name}
                onClick={() => toggleFilter('brand', b.name)}
                data-testid={`filter-brand-${b.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                style={{
                  background: filters.brand === b.name ? '#D90000' : 'rgba(255,255,255,0.05)',
                  border: '1px solid ' + (filters.brand === b.name ? '#D90000' : 'rgba(255,255,255,0.12)'),
                  color: '#fff', padding: '5px 10px', borderRadius: 999,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                {b.name} <span style={{ color: 'rgba(255,255,255,0.5)' }}>({b.count})</span>
              </button>
            ))}
            <span className="overline" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginLeft: 12, marginRight: 6 }}>Category</span>
            {facets.categories.slice(0, 5).map((c) => (
              <button
                key={c.name}
                onClick={() => toggleFilter('category', c.name)}
                data-testid={`filter-category-${c.name.toLowerCase().trim().replace(/[\s/]+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                style={{
                  background: filters.category === c.name ? '#D90000' : 'rgba(255,255,255,0.05)',
                  border: '1px solid ' + (filters.category === c.name ? '#D90000' : 'rgba(255,255,255,0.12)'),
                  color: '#fff', padding: '5px 10px', borderRadius: 999,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                {c.name.trim()}
              </button>
            ))}
            <span className="overline" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, marginLeft: 12, marginRight: 6 }}>Price</span>
            {PRICE_BANDS.map((b) => (
              <button
                key={b.label}
                onClick={() => toggleFilter('priceBand', b.label)}
                data-testid={`filter-price-${b.label.toLowerCase().replace(/[\s$–+]/g, '-')}`}
                style={{
                  background: filters.priceBand === b.label ? '#D90000' : 'rgba(255,255,255,0.05)',
                  border: '1px solid ' + (filters.priceBand === b.label ? '#D90000' : 'rgba(255,255,255,0.12)'),
                  color: '#fff', padding: '5px 10px', borderRadius: 999,
                  fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.12s',
                }}
              >
                {b.label}
              </button>
            ))}
            {hasFilters && (
              <button
                onClick={clearFilters}
                data-testid="catalog-clear-filters"
                style={{ background: 'transparent', border: 'none', color: '#D90000', padding: '5px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {/* Grid */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 24px 80px' }}>
        {loading && results.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
            <Loader2 size={28} className="animate-spin" style={{ margin: '0 auto 12px' }} /> Loading catalog…
          </div>
        ) : results.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
            No products matched "{query}". Try a style code or brand name.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 18 }}>
            {results.map((p) => (
              <button
                key={p.styleID}
                onClick={() => openProduct(p)}
                data-testid={`catalog-product-${p.styleID}`}
                style={{
                  background: '#141414', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
                  padding: 14, cursor: 'pointer', textAlign: 'left', color: '#fff',
                  transition: 'border-color 0.15s, transform 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D90000'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.transform = 'none'; }}
              >
                <div style={{ height: 220, background: '#0A0A0A', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, overflow: 'hidden' }}>
                  {p.styleImageUrl && (
                    <img
                      src={p.styleImageUrl}
                      alt={p.styleName}
                      onError={handleImgError}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                  )}
                </div>
                <div className="overline" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginBottom: 3 }}>
                  {p.brandName} · {p.styleID}
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.35, minHeight: 40, color: '#fff' }}>
                  {p.styleName}
                </div>
                <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>from {fmt(p.basePrice * markup)}</span>
                  <span style={{ fontSize: 10, background: 'rgba(217,0,0,0.15)', color: '#FF4444', padding: '3px 8px', borderRadius: 999, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                    {p.colorCount} colors
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Color picker modal */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setSelected(null)} data-testid="catalog-color-modal">
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#141414', borderRadius: 8, width: 'min(700px, 100%)', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="overline" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>{selected.brandName} · {selected.styleID}</div>
                <div className="heading-font" style={{ fontSize: 20, fontWeight: 700, marginTop: 2 }}>{selected.styleName}</div>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: 'none', padding: 4, cursor: 'pointer' }} data-testid="catalog-color-close">
                <X size={20} color="rgba(255,255,255,0.6)" />
              </button>
            </div>
            <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>
              <div style={{ display: 'flex', gap: 18, marginBottom: 20, alignItems: 'flex-start' }}>
                <div style={{ width: 140, height: 140, background: '#0A0A0A', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                  {selected.styleImageUrl && <img src={selected.styleImageUrl} alt="" onError={handleImgError} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 6, lineHeight: 1.5, maxHeight: 72, overflow: 'hidden' }}>
                    {selected.description || 'Blank apparel piece — design it below.'}
                  </div>
                  <div style={{ fontSize: 13, color: '#fff' }}>
                    Starts at <strong>{fmt(selected.basePrice * markup)}</strong> · Sizes: {(selected.sizes || []).join(', ') || '—'}
                  </div>
                </div>
              </div>
              <div className="overline" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginBottom: 10 }}>Pick a color to start designing</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: 10 }}>
                {(selected.colors || []).map((c) => {
                  const hex = c.hex && !String(c.hex).includes('PROCESS') && !String(c.hex).includes(' ') ? c.hex : '#334155';
                  return (
                    <button
                      key={c.colorName}
                      onClick={() => pickColorAndDesign(selected, c)}
                      data-testid={`catalog-color-${(c.colorName || '').toLowerCase().replace(/\s+/g, '-')}`}
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: 8, cursor: 'pointer', textAlign: 'center', color: '#fff', transition: 'all 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D90000'; e.currentTarget.style.background = 'rgba(217,0,0,0.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: 999, background: hex, border: '2px solid rgba(255,255,255,0.15)', margin: '0 auto 8px' }} />
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>{c.colorName}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                <Sparkles size={12} style={{ verticalAlign: 'middle', marginRight: 4 }} /> Instant estimate after you pick a color.
              </div>
              <ChevronRight size={16} color="rgba(255,255,255,0.4)" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicCatalog;
